<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { ref } from "vue";
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import Loading from 'vue-loading-overlay';
import { Link } from '@inertiajs/vue3';

const props = defineProps({
    year: String,
    userById: Object,
    flash: Object,
    user: Object,
    userlogs: Object,
});

let isLoading = ref(false);
dayjs.extend(advancedFormat);
dayjs.extend(duration);
dayjs.extend(relativeTime);

//function to show human readable time like 1 hour ago
function timeAgo(date) {
    return dayjs(date).fromNow();
}

//function to make the first letter uppercase
function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}


</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="Booking Details" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <h5 class="card-title fw-bold py-3 mb-4">User Details</h5>
            <div class="row">
                <!-- User Sidebar -->
                <div class="col-xl-4 col-lg-5 col-md-5 order-1 order-md-0">
                    <!-- User Card -->
                    <div class="card mb-4">
                        <div class="card-body">
                            <div class="user-avatar-section">
                                <div class="d-flex align-items-center flex-column">
                                    <img class="img-fluid rounded my-4" :src="userById.profile_photo_url" height="110"
                                        width="110" alt="User avatar" />

                                    <div class="user-info text-center">
                                        <h4 class="mb-2">{{ userById.name }}</h4>
                                        <span class="badge bg-label-secondary">{{ userById.email }}</span>
                                    </div>
                                </div>
                            </div>

                            <h5 class="pb-2 border-bottom mb-4">Details</h5>
                            <div class="info-container">
                                <ul class="list-unstyled">
                                    <li class="mb-3">
                                        <span class="fw-bold me-2">Full Name:</span>
                                        <span>{{ userById.name }}</span>
                                    </li>
                                    <li class="mb-3">
                                        <span class="fw-bold me-2">Email:</span>
                                        <span>{{ userById.email }}</span>
                                    </li>
                                    <li class="mb-3">
                                        <span class="fw-bold me-2">Status:</span>
                                        <span v-if="userById.active == 1" class="badge bg-label-success">Active</span>
                                        <span v-else class="badge bg-label-danger">Inactive</span>
                                    </li>
                                    <li class="mb-3">
                                        <span class="fw-bold me-2">Parent:</span>
                                        <span v-if="userById.user_type == 'Company'">{{ userById.company.name }}</span>
                                        <span v-else>{{ userById.agent.agency_name }}</span>
                                    </li>
                                </ul>
                                <div class="d-flex justify-content-center pt-3">
                                    <a href="javascript:;" class="btn btn-primary me-3" data-bs-target="#editUser"
                                        data-bs-toggle="modal">Edit</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-xl-8 col-lg-7 col-md-7 order-0 order-md-1">
                    <!-- User Pills -->
                    <ul class="nav nav-pills flex-column flex-md-row mb-3">
                        <li class="nav-item">
                            <Link class="nav-link active" :href="'/users/show/' + userById.id"><i
                                class="bx bx-user me-1"></i>Activity
                            </Link>
                        </li>
                        <li class="nav-item" v-if="$page.props.user.permissions.includes('view-security-tab')">
                            <Link class="nav-link" :href="'/users/securityTab/' + userById.id"><i
                                class="bx bx-lock-alt me-1"></i>Security
                            </Link>
                        </li>
                        <li class="nav-item" v-if="$page.props.user.permissions.includes('view-permissions-tab')">
                            <Link class="nav-link" :href="'/users/permissionsTab/' + userById.id"><i
                                class="bx bxs-user-badge me-1"></i>Permissions
                            </Link>
                        </li>
                    </ul>

                    <div class="card mb-4">
                        <h5 class="card-header">User Activity Timeline</h5>
                        <div class="card-body">
                            <ul class="timeline">
                                <li v-for="log in props.userlogs" class="timeline-item timeline-item-transparent">
                                    <span class="timeline-point"
                                        :class="log?.event == 'created' ? 'timeline-point-success' : '' || log?.event == 'confirmed' ? 'timeline-point-warning' : '' || log?.event == 'deleted' ? 'timeline-point-danger' : '' || log?.event == 'cancelled' ? 'timeline-point-danger' : '' || log?.event == 'updated' ? 'timeline-point-info' : '' || log?.event == 'reinstated' ? 'timeline-point-secondary' : ''"></span>
                                    <div class="timeline-event">
                                        <div class="timeline-header mb-1">
                                            <h6 class="mb-0">{{ log?.event ? capitalizeFirstLetter(log.event) : '' }}
                                            </h6>
                                            <small class="text-muted">{{
                                                dayjs().from(dayjs(log.created_at), true)
                                                }} ago</small>
                                        </div>
                                        <p class="mb-2">{{ log?.description }}</p>
                                    </div>
                                </li>

                                <li class="timeline-end-indicator">
                                    <i class="bx bx-check-circle"></i>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <!-- /Activity Timeline -->
                </div>
            </div>
        </div>
    </AppLayout>
</template>